import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, CreditCard, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SumUp Solo: stösst über das Backend eine Zahlung am Terminal an. Der Kunde
// hält die Karte an den Leser. Ergebnis kommt per SumUp-Webhook → Backend →
// dieser Dialog pollt und zeigt ✅.
export const SumUpPayDialog = ({ invoice, open, onOpenChange, onPaid }) => {
  const [status, setStatus] = useState('idle'); // idle|starting|waiting|paid|error
  const [paymentId, setPaymentId] = useState(null);
  const pollRef = useRef(null);

  const invoiceId = invoice?.id;
  const amount = invoice ? Number(invoice.total ?? invoice.total_amount ?? invoice.amount ?? 0) : 0;
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const start = useCallback(async () => {
    if (!invoiceId) return;
    setStatus('starting');
    setPaymentId(null);
    try {
      const r = await axios.post(`${API}/payments/sumup/start`,
        { invoice_id: invoiceId, studio_id: invoice?.studio_id }, authHeaders());
      setPaymentId(r.data.payment_id);
      setStatus('waiting');
    } catch (e) {
      setStatus('error');
      toast.error(e.response?.data?.detail || 'SumUp-Zahlung konnte nicht gestartet werden');
    }
  }, [invoiceId, invoice]);

  useEffect(() => {
    if (!open || status !== 'waiting' || !paymentId) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/payments/sumup/${paymentId}`, authHeaders());
        if (r.data?.paid) {
          clearInterval(pollRef.current);
          setStatus('paid');
          toast.success('Zahlung erhalten ✅');
          if (onPaid) onPaid();
        } else if (r.data?.status === 'failed') {
          clearInterval(pollRef.current);
          setStatus('error');
          toast.error('Zahlung fehlgeschlagen / abgebrochen');
        }
      } catch (_) { /* weiter pollen */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [open, status, paymentId, onPaid]);

  useEffect(() => {
    if (open && invoiceId) start();
    if (!open) {
      setStatus('idle');
      setPaymentId(null);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [open, invoiceId, start]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sumup-pay-dialog">
        <DialogHeader>
          <DialogTitle>SumUp Terminal — Karte belasten</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="text-3xl font-bold" data-testid="sumup-pay-amount">CHF {amount.toFixed(2)}</div>
          {invoice?.invoice_number && <div className="text-sm text-muted-foreground">Rechnung {invoice.invoice_number}</div>}

          {(status === 'starting' || status === 'waiting') && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="relative">
                <CreditCard className="w-16 h-16 text-purple-600" />
                <Loader2 className="w-6 h-6 animate-spin text-purple-400 absolute -right-2 -bottom-2" />
              </div>
              <div className="text-sm font-medium text-purple-700">
                {status === 'starting' ? 'Terminal wird vorbereitet…' : 'Bitte Karte an den SumUp-Leser halten…'}
              </div>
            </div>
          )}

          {status === 'paid' && (
            <div className="flex flex-col items-center gap-2 py-6" data-testid="sumup-pay-success">
              <CheckCircle2 className="w-20 h-20 text-green-600" />
              <div className="text-xl font-semibold text-green-700">Bezahlt!</div>
              <Button className="mt-2" onClick={() => onOpenChange(false)} data-testid="sumup-pay-close">Schliessen</Button>
            </div>
          )}

          {status === 'error' && (
            <Button variant="outline" onClick={start} data-testid="sumup-pay-retry" className="my-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Erneut versuchen
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SumUpPayDialog;
